import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject, interval, forkJoin } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Venue } from '../../_shared/models/csTypes/venue';
import { NavController } from '@ionic/angular';
import { User } from '../../_shared/models/user.model';
import { Court } from '../../_shared/models/csTypes/court';
import { Camera } from '../../_shared/models/camera.model';
import { ActivatedRoute } from '@angular/router';
import { VideoBoardSettings } from '../../_shared/models/csTypes/videoSetting';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
@Injectable({
  providedIn: 'root'
})
export class VenueDetailsService {
  private httpClient = inject(HttpClient);
  private navController = inject(NavController);
  private activatedRoute = inject(ActivatedRoute);

  venue = signal<Venue>(undefined);

  venue$: Observable<Venue | null> = this.activatedRoute.params.pipe(
    takeUntilDestroyed(),
    switchMap(params => this.httpClient.get<any>('api_endpoint' + `/venue/${params['_venueId']}`)),
    catchError(() => this.navController.navigateBack('')),
    map(res => res.data),
    tap(venue => {
      if (venue!==null){
        this.venue.set(venue);
      }
      
    })
  );

  readonlyVenue = toSignal(this.venue$);

  // Create Courts 
  createCourts$ = new Subject<{ surfaceType: string, courtName: string, count: number, startAt: number }>();
  venueWithUpdatedCourts$ = this.createCourts$.pipe(
    switchMap((courtObject) => this.createCourts(courtObject))
  )

  // Delete Courts
  deleteCourts$ = new Subject<string[]>();
  venueWithDeletedCourts$ = this.deleteCourts$.pipe(
    switchMap(_courtIds => this.deleteCourts(_courtIds))
  )

//Update Courts
  updateCourt$ = new Subject<Court>();
  venueWithEditedCourts$ = this.updateCourt$.pipe(
    switchMap(courtUpdatePayload => this.updateCourt(courtUpdatePayload))
  );
  

  // Add device to a court
  addDeviceToCourt$ = new Subject<{_courtId: string, camera: Camera}>();
  venueWithAddedDevice$ = this.addDeviceToCourt$.pipe(
    switchMap(props => this.addCourtDevice(props._courtId, props.camera))
  )

  // Remove device from a court
  removeDeviceFromCourt$ = new Subject<{_courtId: string, _deviceId: string}>();
  venueWithRemovedDevice$ = this.removeDeviceFromCourt$.pipe(
    switchMap(props => this.removeCourtDevice(props._courtId, props._deviceId))
  )

  // Add video board to a court
  addVideoBoardToCourt$ = new Subject<{_courtId: string, _videoBoardId: string}>();
  venueWithAddedVideoBoard$ = this.addVideoBoardToCourt$.pipe(
    switchMap(props => this.addCourtVideoBoard(props._courtId, props._videoBoardId))
  )

  // Remove video board from a court
  removeVideoBoardFromCourt$ = new Subject<{_courtId: string, _videoBoardId: string}>();
  venueWithRemovedVideoBoard$ = this.removeVideoBoardFromCourt$.pipe(
    switchMap(props => this.removeCourtVideoBoard(props._courtId, props._videoBoardId))
  )

  // Add Integration
  addIntegration$ = new Subject<{receiverToken: string, partner: string}>();
  venueWithAddedIntegrations$ = this.addIntegration$.pipe(
    switchMap(props => this.addIntegration(props.receiverToken, props.partner))
  )

  // Add Integration
  deleteIntegration$ = new Subject<{_integrationId: string }>();
  venueWithDeletedIntegration$ = this.deleteIntegration$.pipe(
    switchMap(props => this.deleteIntegration(props._integrationId))
  )

  // Add admin
  addAdmin$ = new Subject<{phone: string, email: string }>();
  venueWithAddedAdmin$: Observable<User> = this.addAdmin$.pipe(
    switchMap(props => this.addAdmin(props.phone, props.email))
  )

  // Add admin
  deleteAdmins$ = new Subject<{_adminIds: string[] }>();
  venueWithDeletedAdmin$: Observable<Venue> = this.deleteAdmins$.pipe(
    switchMap(props => this.deleteAdmins(props._adminIds))
  )

  edit(venueName: string, venueAbbreviation: string): Observable<Venue> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/edit`, { venueName, venueAbbreviation }).pipe(
      map(res => res.data)
    );
  }

  deleteVenue(): Observable<Venue> {
    return this.httpClient.delete<any>('api_endpoint' + `/venue/${this.venue()._id}`).pipe(
      map(res => res.data)
    );
  }

  private createCourts(courtObject): Observable<Venue> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/court/create`, courtObject).pipe(
      map(res => res.data),
      tap(venueWithCreatedCourts => this.venue.update((venue) => {
        return {...venue, courts: venueWithCreatedCourts.courts }
      }))
    )
  }

  private deleteCourts(_courtIds): Observable<Venue> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/court/delete`, { _courtIds }).pipe(
      map(res => res.data),
      tap(venueWithCreatedCourts => this.venue.update((venue) => {
        return {...venue, courts: venueWithCreatedCourts.courts }
      }))
    );
  }

  private updateCourt(court: Court): Observable<Venue> {
    const venueId = this.venue()._id;
  
    return this.httpClient
        .put<{data: Venue}>('api_endpoint' + `/venue/${venueId}/court/update`, { venueId, ...court, _courtId: court._id, _id:undefined })
        .pipe(map((res) => res.data), tap((venue) => {
          this.venue.set(venue);
        }))

  }
  
  private addCourtDevice( _courtId: string, camera: Camera): Observable<Court> {    
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/court/${_courtId}/device/add`, { camera }).pipe(
      map(res => res.data),
      tap(court => this.venue.update((venue) => {
        const index = venue.courts?.findIndex(c => c._id === _courtId);        
        venue.courts[index] = court;        
        return {...venue, courts: venue.courts}
      }))
    );
  }

  private removeCourtDevice(_courtId: string, _deviceId: string): Observable<Court> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/court/${_courtId}/device/remove`, { _deviceId }).pipe(
      map(res => res.data),
      tap(court => this.venue.update((venue) => {
        const index = this.venue().courts?.findIndex(c => c._id === _courtId);        
        venue.courts[index] = court;
        return {...venue, courts: venue.courts}
      }))
    );
  }

  private addCourtVideoBoard( _courtId: string, _videoBoardId: string): Observable<Court> {    
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/court/${_courtId}/video-board/add`, { _videoBoardId }).pipe(
      map(res => res.data),
      tap(court => this.venue.update((venue) => {
        const index = venue.courts.findIndex(c => c._id === _courtId);        
        venue.courts[index] = court;        
        return {...venue, courts: venue.courts}
      }))
    );
  }

  private removeCourtVideoBoard(_courtId: string, _videoBoardId: string): Observable<Court> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/court/${_courtId}/video-board/remove`, { _videoBoardId }).pipe(
      map(res => res.data),
      tap(court => this.venue.update((venue) => {
        const index = this.venue().courts?.findIndex(c => c._id === _courtId);        
        venue.courts[index] = court;
        return {...venue, courts: venue.courts}
      }))
    );
  }

  private addIntegration(receiverToken: string, partner: string): Observable<Venue> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/integration/add`, { receiverToken, partner }).pipe(
      map(res => res.data),
      tap(venueWithAddedIntegrations => this.venue.update((venue) => {        
        return {...venue, integrations: venueWithAddedIntegrations.integrations }
      }))
    );
  }

  private deleteIntegration(_integrationId): Observable<User> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/integration/delete`, { _integrationId }).pipe(
      map(res => res.data),
      tap(() => this.venue.update((venue) => {
        const index = this.venue().integrations.findIndex(c => c._id === _integrationId);
        venue.integrations.splice(index, 1);
        return {...venue, integrations: venue.integrations}
      }))
    );
  }

  private addAdmin(phone: string, email: string): Observable<User> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/admin/add`, { phone, email }).pipe(
      map(res => res.data),
      tap(user => this.venue.update((venue) => {        
        venue.owners.push(user);
        return { ...venue }
      }))
    );
  }

  private deleteAdmins(_adminIds): Observable<Venue> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/admin/delete`, { _adminIds }).pipe(
      map(res => res.data),
      tap(venueWithDeletedOwners => this.venue.update((venue) => {        
        return {...venue, owners: venueWithDeletedOwners.owners }
      }))
    );
  }

  public videoBoardUploadLogo(formdata: FormData): Observable<VideoBoardSettings> {
    return this.httpClient.post<any>('api_endpoint' + `/venue/${this.venue()._id}/venue-setting`, formdata ).pipe(
      map(res => res?.data)
    );
  }
}
